import React, { type ReactElement } from 'react';

import { type Event, EventTypeEnum, ParticipantTypeEnum } from '@clubspark-node/clubspark-tennis-types';

import { type LevelConfiguration } from 'atoms/tournament';
import {
  RatingTypeEnum,
  ScaleTypeEnum,
} from 'components/panels/eventsSplitDraws/selectedEvent/rankingsAndRatings/types';
import { type ScaleItem } from 'typedefs/store/tmxTypes';

import { type ParticipantScaling, type ScalingPurpose } from '.';
import Scaling from './Scaling';

const scaleName = RatingTypeEnum.NTRP;
const scaleType = ScaleTypeEnum.RATING;

/* TODO: Remove reliance on this? Is it needed? */
export const isNtrpEvent = (ratingCategory) => ratingCategory?.ratingType === RatingTypeEnum.NTRP;

export const getNtrpLevel = (ratingCategory) => {
  if (ratingCategory && ratingCategory.value) {
    const rating = ratingCategory?.value.toString();
    const formattedRating = rating.length === 1 ? rating + '_0' : rating.replace('.', '_');
    return 'L' + formattedRating;
  }

  return null;
};

export const getRatingDisplay = (scaleItem: NTRPScaleItem): number => scaleItem?.scaleValue?.ntrpRating;

export const getRatingValue = (scaleItem: NTRPScaleItem): number => {
  const dntrp = scaleItem?.scaleValue?.dntrpRatingHundredths ?? '0';
  return Number.isFinite(+dntrp) ? parseFloat(dntrp) : 0;
};

export const getBottomRatingTypeValue = (scaleItem: NTRPScaleItem): number => {
  const ustaRatingType = scaleItem?.scaleValue?.ustaRatingType;
  if (ustaRatingType === 'T') return 1;
  if (ustaRatingType === 'S') return 2;
  return 0;
};

export interface NTRPScaleItem extends ScaleItem {
  scaleValue: {
    ntrpLevel: string;
    ntrpRating: number;
    ustaRatingType: string;
    dntrpRatingHundredths: string;
  };
}

const valueOrPlaceholder = (value, text) => (Number.isFinite(value) ? text : '-');

class NTRPScaling extends Scaling {
  static scaleName = scaleName;
  static scaleType = scaleType;

  static scaleSortMethod = (
    purpose: ScalingPurpose,
    a: ParticipantScaling<NTRPScaleItem>,
    b: ParticipantScaling<NTRPScaleItem>,
  ): number =>
    b.scaleItems.length - a.scaleItems.length ||
    this.getBottomRatingTypeValues(a) - this.getBottomRatingTypeValues(b) ||
    this.getValue(b) - this.getValue(a);

  public static getDisplay({ scaleItems }: ParticipantScaling<NTRPScaleItem>): ReactElement {
    if (scaleItems.length <= 2) {
      return (
        <>
          {scaleItems.map((scaleItem, index) => {
            const value = getRatingDisplay(scaleItem);
            const bottomRatingType = !!getBottomRatingTypeValue(scaleItem);
            return (
              <span key={`${index}`} style={{ display: 'block' }}>
                {valueOrPlaceholder(
                  value,
                  `${value?.toFixed(2)}${bottomRatingType ? scaleItem?.scaleValue?.ustaRatingType : ''}`,
                )}
              </span>
            );
          })}
        </>
      );
    }
    // Using mean average
    const values = scaleItems?.map(getRatingDisplay);
    const value = values?.reduce((total, rating) => total + rating, 0) / values.length;
    return valueOrPlaceholder(value, value?.toFixed(2));
  }

  public static getText({ scaleItems }: ParticipantScaling<NTRPScaleItem>): string {
    if (scaleItems.length <= 2) {
      return scaleItems
        .map((scaleItem) => {
          const value = getRatingDisplay(scaleItem);
          const bottomRatingType = !!getBottomRatingTypeValue(scaleItem);
          return valueOrPlaceholder(
            value,
            `${value?.toFixed(2)}${bottomRatingType ? scaleItem?.scaleValue?.ustaRatingType : ''}`,
          );
        })
        .join(', ');
    }
    // Using mean average
    const values = scaleItems.map(getRatingDisplay);
    const value = values.reduce((total, rating) => total + rating, 0) / values.length;
    return valueOrPlaceholder(value, value?.toFixed(2));
  }

  public static getValue({ participant, scaleItems }: ParticipantScaling<NTRPScaleItem>): number {
    const values = scaleItems.map(getRatingValue);
    const value = values.reduce((total, rating) => total + rating, 0) || 0;
    return participant?.participantType === ParticipantTypeEnum.PAIR ? value : value / values.length;
  }

  public static getBottomRatingTypeValues = ({ scaleItems }: ParticipantScaling<NTRPScaleItem>): number =>
    Math.max.apply(
      null,
      scaleItems.map((scaleItem) => getBottomRatingTypeValue(scaleItem)),
    );

  static getIsAvailable(event: Event, levelConfig?: Partial<LevelConfiguration>) {
    const isNtrpEnabled = levelConfig?.ntrpEnabled !== false;
    const isTeam = event?.eventType === EventTypeEnum.TEAM;
    return isNtrpEnabled && !isTeam;
  }

  public static getDisplayName = () => 'NTRP';
}

export default NTRPScaling;
